// 🔴 사원결제 진단 파이프 — client 이벤트를 Shopify App Proxy(same-origin)로 전송.
//
//   왜 same-origin(`/apps/sf/...`)인가:
//     한도조회(`/staff/remaining-public`)는 cross-origin host(lkk-mhk-store-api.mistobrand.com)로
//     나간다. 실패의 상당수는 그 host 로의 요청/응답 왕복이 특정 해외 모바일 네트워크에서
//     깨지는 경우다. 진단을 같은 cross-origin host 로 보내면 같은 이유로 진단도 유실된다.
//     App Proxy 는 storefront 자기 도메인(first-party)이라 그 상황에서도 살아남아
//     "요청이 서버에 도달조차 못했다"는 사실을 기록한다.
//
//   전송: navigator.sendBeacon 우선(unload 생존) → fetch({keepalive}) fallback.
//   상관: session_trace_id(1 tab-session, reload 생존) + BE 가 재구성하는 trace_id.
//   PII: raw employee_no 는 envelope 로만 보내고 BE 가 hash+trace_id 로 변환 후 폐기.
//        email 은 절대 안 보냄. 값 대신 presence bool 로 config 누락을 진단.
//
//   BE: misto-storefront POST /storefront/proxy/diag → relay-logs(Stage.EMPLOYEE_PAYMENT_DIAG).
//   박제: docs/be-handoff — employee-payment-diag, plan tw-shopify-majestic-fog.
(function () {
  // 🔴 진단 수집 경로 — cross-origin BE 직송 (게이트웨이 앱 없이 즉시 가동).
  //   근거: 확정 실패 모드가 "요청 도달·성공, 응답 미복귀"라 outbound 는 정상 → beacon 이 서버에 꽂힘.
  //   (same-origin App Proxy 는 "outbound 완전 차단" 대비였는데 prod 로그가 그 가설을 반증.)
  //   게이트웨이 앱 설치 후 same-origin(/apps/sf/storefront/proxy/diag)으로 flip 하면 됨.
  const DIAG_URL = ((window.MISTO_EMPLOYEE || {}).api_base || 'https://lkk-mhk-store-api.mistobrand.com') + '/storefront/proxy/diag';

  // ── session id: 1/tab-session, reload 생존 ────────────────────
  function getSessionTraceId() {
    const k = 'diag_session_trace_id';
    let id = null;
    try { id = sessionStorage.getItem(k); } catch (e) { return 'sess-nostore'; }
    if (!id) {
      id = (window.crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      try { sessionStorage.setItem(k, id); } catch (e) {}
    }
    return id;
  }

  // ── 전송: sendBeacon → fetch keepalive ────────────────────────
  function post(bodyObj) {
    let body;
    try { body = JSON.stringify(bodyObj); } catch (e) { return false; }
    if (navigator.sendBeacon) {
      try {
        // text/plain = simple request → cross-origin 이어도 preflight 없음(BE 는 content-type 무관 json 파싱). unload 생존.
        if (navigator.sendBeacon(DIAG_URL, new Blob([body], { type: 'text/plain' }))) return true;
      } catch (e) { /* fall through */ }
    }
    try {
      fetch(DIAG_URL, {
        method: 'POST', body: body, keepalive: true,
        headers: { 'Content-Type': 'text/plain' },
      }).catch(function () {});
      return true;
    } catch (e) { return false; }
  }

  // ── base 이벤트 (presence bool — 값 미노출) ────────────────────
  function baseEvent(event, fields) {
    const cfg = window.MISTO_EMPLOYEE || {};
    const e = {
      event: event,
      ts_client: Date.now(),
      page: location.pathname,
      is_employee: !!cfg.is_employee,
      api_base_present: !!cfg.api_base,
      employee_no_present: !!cfg.employee_no,
      employee_store_code_present: !!cfg.employee_store_code,
      user_agent: (navigator.userAgent || '').slice(0, 300),
    };
    if (fields) { for (const k in fields) { if (fields[k] !== undefined) e[k] = fields[k]; } }
    return e;
  }

  function envelope(events) {
    const cfg = window.MISTO_EMPLOYEE || {};
    return {
      v: 1,
      session_trace_id: getSessionTraceId(),
      shop_domain: (window.Shopify && Shopify.shop) || null,
      employee_no: cfg.employee_no || null,           // raw → BE hash+trace_id 후 폐기
      employee_store_code: cfg.employee_store_code || null,
      events: events,
    };
  }

  // 항상-emit / unload 인접 이벤트는 batch-of-1 즉시 전송(유실 방지).
  function emitDiag(event, fields) {
    try { return post(envelope([baseEvent(event, fields)])); } catch (e) { return false; }
  }

  // ── page_boot 스로틀: employee 후보 + session 1회 ─────────────
  function maybeBoot() {
    const cfg = window.MISTO_EMPLOYEE || {};
    if (!(cfg.is_employee || cfg.employee_no)) return;      // 후보 아님 → skip (익명/일반고객)
    try { if (sessionStorage.getItem('diag_page_boot_sent') === '1') return; } catch (e) {}
    try { sessionStorage.setItem('diag_page_boot_sent', '1'); } catch (e) {}

    let stored = null, failPending = false, exceedActive = false;
    try {
      stored = sessionStorage.getItem('employee_payment_mode');
      failPending = sessionStorage.getItem('employee_limit_fail_pending') === '1';
      exceedActive = sessionStorage.getItem('employee_limit_exceed_active') === '1';
    } catch (e) {}

    emitDiag('page_boot', {
      stored_mode: stored,
      fail_pending: failPending,
      exceed_active: exceedActive,
      theme_store_code: cfg.employee_store_code || null,    // 10050 vs 10049 (HK-sync 회귀 탐지)
    });

    // config 준비/누락 — 사원인데 employee_no 추출 실패면 tag/metafield/theme config 문제.
    if (cfg.is_employee && cfg.api_base && cfg.employee_store_code && cfg.employee_no) {
      emitDiag('employee_config_ready', {
        employee_store_code: cfg.employee_store_code,
        api_base_host: (function () { try { return new URL(cfg.api_base).host; } catch (e) { return null; } })(),
      });
    } else if (cfg.is_employee || cfg.employee_no) {
      emitDiag('employee_config_missing', { caller: 'boot' });
    }
  }

  window.MISTO_DIAG = { emit: emitDiag, sessionTraceId: getSessionTraceId };
  try { maybeBoot(); } catch (e) {}
})();
